import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import axios from "../utils/axiosConfig";
import toast from "react-hot-toast";

const AnnouncementContext = createContext();

export const useAnnouncements = () => {
  const context = useContext(AnnouncementContext);
  if (!context) {
    throw new Error(
      "useAnnouncements must be used within AnnouncementProvider",
    );
  }
  return context;
};

export const AnnouncementProvider = ({ children }) => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [announcements, setAnnouncements] = useState([]);
  const [popupAnnouncement, setPopupAnnouncement] = useState(null);
  const [barAnnouncements, setBarAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const userId = user?._id || user?.id;

  // Track which popups have been shown in this session (to prevent auto-closing)
  const [shownPopupIds, setShownPopupIds] = useState(() => {
    const saved = sessionStorage.getItem("shownPopupAnnouncements");
    return saved ? JSON.parse(saved) : [];
  });

  // Save shown popup IDs to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(
      "shownPopupAnnouncements",
      JSON.stringify(shownPopupIds),
    );
  }, [shownPopupIds]);

  // Load from localStorage on mount
  useEffect(() => {
    const cachedAnnouncements = localStorage.getItem("cachedAnnouncements");
    if (cachedAnnouncements) {
      try {
        const parsed = JSON.parse(cachedAnnouncements);
        setAnnouncements(parsed);

        // Filter out dismissed announcements
        const dismissedIds = JSON.parse(
          localStorage.getItem("dismissedAnnouncements") || "[]",
        );
        const activeAnnouncements = parsed.filter(
          (ann) => !dismissedIds.includes(ann._id),
        );

        // Only show popup if not already shown in this session
        const popup = activeAnnouncements.find(
          (ann) => ann.showAsPopup === true && !shownPopupIds.includes(ann._id),
        );
        setPopupAnnouncement(popup || null);

        const bars = activeAnnouncements.filter(
          (ann) => ann.showAsPopup !== true,
        );
        setBarAnnouncements(bars);

        const unread = activeAnnouncements.filter((ann) => {
          return !ann.seenBy?.some((s) => s.user === userId);
        }).length;
        setUnreadCount(unread);
      } catch (e) {
        console.error("Failed to parse cached announcements", e);
      }
    }
  }, []);

  const fetchAnnouncements = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/announcements/active", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        const announcementsData = response.data.announcements || [];
        setFeatureEnabled(response.data.enabled !== false);

        // Get dismissed IDs from localStorage
        const dismissedIds = JSON.parse(
          localStorage.getItem("dismissedAnnouncements") || "[]",
        );

        // Filter out dismissed announcements
        const activeAnnouncements = announcementsData.filter(
          (ann) => !dismissedIds.includes(ann._id),
        );

        // Cache in localStorage
        localStorage.setItem(
          "cachedAnnouncements",
          JSON.stringify(activeAnnouncements),
        );

        setAnnouncements(activeAnnouncements);
        setUnreadCount(response.data.unreadCount || 0);

        // Only show popup if not already shown in this session
        const popup = activeAnnouncements.find(
          (ann) => ann.showAsPopup === true && !shownPopupIds.includes(ann._id),
        );
        setPopupAnnouncement(popup || null);

        const bars = activeAnnouncements.filter(
          (ann) => ann.showAsPopup !== true,
        );
        setBarAnnouncements(bars);
      }
    } catch (error) {
      console.error("Failed to fetch announcements:", error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, shownPopupIds]);

  const markAsSeen = useCallback(
    async (announcementId) => {
      if (!user) return;

      try {
        const token = localStorage.getItem("token");
        await axios.post(
          `/api/announcements/${announcementId}/seen`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );

        // Update local state and cache
        setAnnouncements((prev) => {
          const updated = prev.map((ann) =>
            ann._id === announcementId
              ? {
                  ...ann,
                  seenBy: [
                    ...(ann.seenBy || []),
                    { user: userId, seenAt: new Date() },
                  ],
                }
              : ann,
          );
          localStorage.setItem("cachedAnnouncements", JSON.stringify(updated));
          return updated;
        });

        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error("Failed to mark announcement as seen:", error);
      }
    },
    [user, userId],
  );

  const dismissAnnouncement = useCallback(
    async (announcementId) => {
      if (!user) return;

      try {
        const token = localStorage.getItem("token");
        await axios.post(
          `/api/announcements/${announcementId}/dismiss`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );

        // Add to dismissed IDs in localStorage
        const dismissedIds = JSON.parse(
          localStorage.getItem("dismissedAnnouncements") || "[]",
        );
        if (!dismissedIds.includes(announcementId)) {
          dismissedIds.push(announcementId);
          localStorage.setItem(
            "dismissedAnnouncements",
            JSON.stringify(dismissedIds),
          );
        }

        // Update local state and cache
        setAnnouncements((prev) => {
          const updated = prev.filter((ann) => ann._id !== announcementId);
          localStorage.setItem("cachedAnnouncements", JSON.stringify(updated));
          return updated;
        });

        setPopupAnnouncement((prev) =>
          prev?._id === announcementId ? null : prev,
        );
        setBarAnnouncements((prev) =>
          prev.filter((ann) => ann._id !== announcementId),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));

        toast.success("Announcement dismissed");
      } catch (error) {
        console.error("Failed to dismiss announcement:", error);
        toast.error("Failed to dismiss");
      }
    },
    [user],
  );

  const closePopup = useCallback(() => {
    if (popupAnnouncement) {
      // Mark this popup as shown in this session so it won't reappear on refresh
      setShownPopupIds((prev) => [...prev, popupAnnouncement._id]);
      setPopupAnnouncement(null);
    }
  }, [popupAnnouncement]);

  const refreshAnnouncements = useCallback(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchAnnouncements();
    }
  }, [isAuthenticated, user, fetchAnnouncements]);

  const value = {
    announcements,
    popupAnnouncement,
    barAnnouncements,
    loading,
    unreadCount,
    featureEnabled,
    markAsSeen,
    dismissAnnouncement,
    closePopup, // Add this to context
    refreshAnnouncements,
  };

  return (
    <AnnouncementContext.Provider value={value}>
      {children}
    </AnnouncementContext.Provider>
  );
};
